import * as y from "yup";
import type { AudienceData } from "@common/interfaces/KYDMarketing";
import type { FacebookPage, InstagramAccount } from "../types";

export const PRIMARY_TEXT_MAX = 1000;

const creativeSchema = y.object({
  name: y.string().optional(),
  squareMediaUrl: y.string().nullable().optional(),
  squareMediaFileKey: y.string().nullable().optional(),
  squareMediaType: y
    .string()
    .oneOf(["image", "video"])
    .nullable()
    .optional(),
  verticalMediaUrl: y.string().nullable().optional(),
  verticalMediaFileKey: y.string().nullable().optional(),
  verticalMediaType: y
    .string()
    .oneOf(["image", "video"])
    .nullable()
    .optional(),
  primaryText: y
    .string()
    .required("Primary text is required")
    .max(
      PRIMARY_TEXT_MAX,
      `Primary text must be at most ${PRIMARY_TEXT_MAX} characters`
    ),
  fbPage: y
    .mixed<FacebookPage | null>()
    .nullable()
    .test("fb-page-required", "Facebook Page is required", (value) =>
      Boolean(value?.id)
    ),
  instagramAccount: y
    .mixed<InstagramAccount | null>()
    .nullable()
    .test("instagram-account-required", "Instagram Account is required", (value) =>
      Boolean(value?.id)
    ),
})
.test(
  "creative-square-media",
  "Square creative media is required",
  (value) =>
    Boolean(
      (value?.squareMediaUrl || value?.squareMediaFileKey) &&
        value?.squareMediaType
    )
)
.test(
  "creative-vertical-media",
  "Vertical creative media is required",
  (value) =>
    Boolean(
      (value?.verticalMediaUrl || value?.verticalMediaFileKey) &&
        value?.verticalMediaType
    )
);

export const CreateCampaignFlowSchema = y.object({
  creatives: y
    .array()
    .of(creativeSchema)
    .min(1, "At least one creative is required")
    .required(),
  smart_targeting: y.boolean().required(),
  custom_audiences: y
    .array()
    .of(y.mixed<AudienceData>().required())
    .when("smart_targeting", {
      is: false,
      then: (schema) =>
        schema.min(
          1,
          "At least one custom audience is required when Smart Targeting is disabled"
        ),
      otherwise: (schema) => schema,
    }),
  audience_search_query: y.string().default(""),
  budget: y.object({
    type: y
      .string()
      .oneOf(["daily", "lifetime"])
      .required("Budget type is required"),
    amount: y.object({
      amount: y
        .number()
        .nullable()
        .typeError("Budget amount must be a valid number")
        .test(
          "budget-amount",
          "Budget amount must be greater than 0",
          (value) => (value ?? 0) > 0
        ),
      currency: y.string().required(),
    }),
  }),
  schedule: y.object({
    start_at: y.string().required("Start date is required"),
    end_at: y
      .string()
      .required("End date is required")
      .test(
        "end-after-start",
        "End date must be after start date",
        function (value) {
          const startAt = this.parent.start_at;
          if (!startAt || !value) return false;
          return new Date(startAt) < new Date(value);
        }
      ),
  }),
});

export type CreateCampaignFlowValues = y.InferType<
  typeof CreateCampaignFlowSchema
>;
